import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Divider,
    Avatar
} from '@mui/material';
import { Delete as DeleteIcon, Person as PersonIcon } from '@mui/icons-material';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import tokenService from '../../services/tokenService';
import { canModerateWikiComments } from './wikiPermissions';

interface WikiComment {
    id: number;
    content: string;
    createdBy: number;
    createdByName: string;
    createdAt: string;
    updatedAt: string;
}

interface WikiCommentsProps {
    articleId: number;
}

const WikiComments: React.FC<WikiCommentsProps> = ({ articleId }) => {
    const [comments, setComments] = useState<WikiComment[]>([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { permissions } = usePermissions();

    // #400: "Eigener Kommentar?" ueber den numerischen JWT-userId-Claim —
    // exakt die ID, die der API-CreatedBySubscriber in createdBy schreibt.
    // Der fruehere Vergleich mit identity.id aus useGetIdentity() hing an
    // einem async SSO-userinfo-Fetch (bei Fehler: gar kein Loeschen-Button)
    // und an einer ggf. veralteten Identity aus dem sessionStorage-Cache.
    const currentUserId = tokenService.getUserId();

    // Moderation: Content-Manager/Company-Admin aufwaerts duerfen laut
    // Backend-Security auch fremde Kommentare loeschen.
    const canModerate = canModerateWikiComments(permissions);

    const canDeleteComment = (comment: WikiComment): boolean =>
        canModerate || (currentUserId !== null && Number(comment.createdBy) === currentUserId);

    useEffect(() => {
        loadComments();
    }, [articleId]);

    const loadComments = async () => {
        try {
            const result = await dataProvider.getList('wiki_comments', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'createdAt', order: 'ASC' },
                filter: { 'article.id': articleId }
            });
            setComments(result.data as WikiComment[]);
        } catch (error) {
            console.error('Error loading comments:', error);
            notify('Fehler beim Laden der Kommentare', { type: 'error' });
        }
    };

    const handleSubmitComment = async () => {
        if (!newComment.trim()) {
            notify('Bitte geben Sie einen Kommentar ein', { type: 'warning' });
            return;
        }

        setLoading(true);
        try {
            await dataProvider.create('wiki_comments', {
                data: {
                    article: `/api/wiki_articles/${articleId}`,
                    content: newComment
                }
            });
            setNewComment('');
            await loadComments();
            notify('Kommentar erfolgreich hinzugefügt', { type: 'success' });
        } catch (error) {
            console.error('Error creating comment:', error);
            notify('Fehler beim Erstellen des Kommentars', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteComment = async (commentId: number) => {
        if (!window.confirm('Möchten Sie diesen Kommentar wirklich löschen?')) {
            return;
        }

        try {
            await dataProvider.delete('wiki_comments', {
                id: commentId,
                previousData: {} as any
            });
            await loadComments();
            notify('Kommentar erfolgreich gelöscht', { type: 'success' });
        } catch (error) {
            console.error('Error deleting comment:', error);
            notify('Fehler beim Löschen des Kommentars', { type: 'error' });
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Kommentare ({comments.length})
                </Typography>

                {/* Comment Input */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder="Schreiben Sie einen Kommentar..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        disabled={loading}
                    />
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmitComment}
                            disabled={loading || !newComment.trim()}
                        >
                            Kommentar hinzufügen
                        </Button>
                    </Box>
                </Box>

                {/* Comments List */}
                {comments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        Noch keine Kommentare vorhanden
                    </Typography>
                ) : (
                    <List>
                        {comments.map((comment, index) => (
                            <React.Fragment key={comment.id}>
                                <ListItem
                                    alignItems="flex-start"
                                    secondaryAction={
                                        canDeleteComment(comment) && (
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleDeleteComment(comment.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                        <PersonIcon />
                                    </Avatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle2">
                                                    {comment.createdByName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(comment.createdAt)}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                                            >
                                                {comment.content}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                {index < comments.length - 1 && <Divider variant="inset" component="li" />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </CardContent>
        </Card>
    );
};

export default WikiComments;
